import * as THREE from "three";

/**
 * Procedural pixel-art texture generator for Steve and Alex character models.
 * Matches official Minecraft appearances pixel-for-pixel using un-smoothed canvas textures.
 */

export interface CharacterSkinTextures {
  readonly headFront: THREE.CanvasTexture;
  readonly headBack: THREE.CanvasTexture;
  readonly headSide: THREE.CanvasTexture;
  readonly headTop: THREE.CanvasTexture;
  readonly headBottom: THREE.CanvasTexture;

  readonly torsoFront: THREE.CanvasTexture;
  readonly torsoBack: THREE.CanvasTexture;
  readonly torsoSide: THREE.CanvasTexture;
  readonly torsoTopBottom: THREE.CanvasTexture;

  readonly armFront: THREE.CanvasTexture;
  readonly armSide: THREE.CanvasTexture;
  readonly armTopBottom: THREE.CanvasTexture;
  readonly armHandBottom: THREE.CanvasTexture;

  readonly legFront: THREE.CanvasTexture;
  readonly legSide: THREE.CanvasTexture;
  readonly legTopBottom: THREE.CanvasTexture;

  readonly fpArm: THREE.CanvasTexture;
}

function makeTexture(
  w: number,
  h: number,
  rows: readonly string[],
  palette: Readonly<Record<string, string>>,
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    for (let y = 0; y < h; y++) {
      const row = rows[y] || "";
      for (let x = 0; x < w; x++) {
        const ch = row[x];
        if (ch && palette[ch]) {
          ctx.fillStyle = palette[ch];
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  return texture;
}

// ---------------------------------------------------------------------------
// STEVE SKIN DEFINITION
// ---------------------------------------------------------------------------

const STEVE_PALETTE: Readonly<Record<string, string>> = {
  // Hair & beard
  "0": "#2B1A0E", // Darkest shadow hair
  "1": "#3C2415", // Base dark brown hair
  "2": "#4E331E", // Mid brown hair
  "3": "#5C3E25", // Highlight hair
  "B": "#442813", // Beard / goatee
  "M": "#62371E", // Mouth / lip
  // Skin
  "4": "#A0673D", // Skin shadow
  "5": "#B5784C", // Mid skin
  "6": "#C48A5E", // Base tanned skin
  "7": "#D1986B", // Skin highlight
  // Eyes
  "W": "#FFFFFF", // Sclera white
  "E": "#463682", // Violet-blue pupil
  // Shirt (Turquoise / Cyan)
  "T": "#0FA1A6", // Shadow cyan
  "U": "#12B3B8", // Base cyan
  "V": "#1AC4C9", // Highlight cyan
  // Pants (Indigo-blue / Purple-blue)
  "P": "#382F92", // Shadow pants
  "Q": "#453DB5", // Base purple-blue pants
  "R": "#524AC4", // Highlight pants
  // Shoes (Gray)
  "X": "#404040", // Dark gray
  "Y": "#555555", // Mid gray
  "Z": "#6A6A6A", // Light gray
};

export function createSteveTextures(): CharacterSkinTextures {
  const headFront = makeTexture(
    8,
    8,
    [
      "12111121",
      "12211221",
      "16666661",
      "66WE6EW6",
      "66655666",
      "66BMMB66",
      "6BBBBBB6",
      "66BBBB66",
    ],
    STEVE_PALETTE,
  );

  const headBack = makeTexture(
    8,
    8,
    [
      "11211211",
      "12211221",
      "21111112",
      "11211211",
      "11111111",
      "12111121",
      "01111110",
      "00111100",
    ],
    STEVE_PALETTE,
  );

  const headSide = makeTexture(
    8,
    8,
    [
      "11111111",
      "12111121",
      "12111111",
      "11666611",
      "11666661",
      "11B66661",
      "01BB6661",
      "001BB661",
    ],
    STEVE_PALETTE,
  );

  const headTop = makeTexture(
    8,
    8,
    [
      "12111121",
      "21122112",
      "11233211",
      "12333321",
      "12333321",
      "11233211",
      "21122112",
      "12111121",
    ],
    STEVE_PALETTE,
  );

  const headBottom = makeTexture(
    8,
    8,
    [
      "66666666",
      "66666666",
      "66666666",
      "66666666",
      "66BBBB66",
      "6BBBBBB6",
      "0BBBBBB0",
      "00BBBB00",
    ],
    STEVE_PALETTE,
  );

  const torsoFront = makeTexture(
    8,
    12,
    [
      "UUV66VUUT",
      "UUUV6VUU",
      "TUUUVUUT",
      "UUUUUUUU",
      "VUUUUTUU",
      "UUUUUUUU",
      "UUUUUUUU",
      "TUUUUUUT",
      "UUUUUUUU",
      "VUUUUUUV",
      "TUUUUUUU",
      "TUUUUTPP",
    ],
    STEVE_PALETTE,
  );

  const torsoBack = makeTexture(
    8,
    12,
    [
      "TUUUUUUT",
      "UUUUUUUU",
      "VUUUUUUV",
      "UUUUUUUU",
      "UUUUTUUU",
      "UUUUUUUU",
      "TUUUUUUT",
      "UUUUUUUU",
      "UUUUUUUU",
      "VUUUUUUV",
      "UUUUTUUU",
      "TUUUUUUT",
    ],
    STEVE_PALETTE,
  );

  const torsoSide = makeTexture(
    4,
    12,
    [
      "TUUU",
      "UUUU",
      "VUUU",
      "UUUT",
      "UUUU",
      "TUUU",
      "UUUU",
      "UUUT",
      "VUUU",
      "UUUU",
      "TUUU",
      "UUUU",
    ],
    STEVE_PALETTE,
  );

  const torsoTopBottom = makeTexture(
    8,
    4,
    [
      "UUUUUUUU",
      "VUUUUUUV",
      "TUUUUUUT",
      "UUUUUUUU",
    ],
    STEVE_PALETTE,
  );

  const armFront = makeTexture(
    4,
    12,
    [
      "TUUU",
      "UUUT",
      "VUUU",
      "UUUU",
      "6666",
      "6766",
      "6656",
      "6666",
      "6766",
      "6656",
      "6666",
      "5665",
    ],
    STEVE_PALETTE,
  );

  const armSide = makeTexture(
    4,
    12,
    [
      "UUUT",
      "TUUU",
      "UUUT",
      "UUUU",
      "6666",
      "6667",
      "6566",
      "6666",
      "6676",
      "5666",
      "6666",
      "5555",
    ],
    STEVE_PALETTE,
  );

  const armTopBottom = makeTexture(
    4,
    4,
    [
      "UUUU",
      "VUUU",
      "UUUT",
      "UUUU",
    ],
    STEVE_PALETTE,
  );

  const armHandBottom = makeTexture(
    4,
    4,
    [
      "6666",
      "6766",
      "6656",
      "6666",
    ],
    STEVE_PALETTE,
  );

  const legFront = makeTexture(
    4,
    12,
    [
      "PQQQ",
      "QQQP",
      "RQQQ",
      "QQQQ",
      "PQQQ",
      "QQQP",
      "RQQQ",
      "QQQQ",
      "PQQQ",
      "QQQP",
      "XYYX",
      "YZZY",
    ],
    STEVE_PALETTE,
  );

  const legSide = makeTexture(
    4,
    12,
    [
      "QQQP",
      "PQQQ",
      "QQQP",
      "QQQQ",
      "QQQP",
      "PQQQ",
      "QQQP",
      "QQQQ",
      "QQQP",
      "PQQQ",
      "YXXY",
      "ZZZZ",
    ],
    STEVE_PALETTE,
  );

  const legTopBottom = makeTexture(
    4,
    4,
    [
      "QQQQ",
      "RQQQ",
      "QQQP",
      "QQQQ",
    ],
    STEVE_PALETTE,
  );

  const fpArm = makeTexture(
    4,
    12,
    [
      "6666",
      "6766",
      "6656",
      "6666",
      "6766",
      "6656",
      "6666",
      "5665",
      "6666",
      "6766",
      "6656",
      "5555",
    ],
    STEVE_PALETTE,
  );

  return {
    headFront,
    headBack,
    headSide,
    headTop,
    headBottom,
    torsoFront,
    torsoBack,
    torsoSide,
    torsoTopBottom,
    armFront,
    armSide,
    armTopBottom,
    armHandBottom,
    legFront,
    legSide,
    legTopBottom,
    fpArm,
  };
}

// ---------------------------------------------------------------------------
// ALEX SKIN DEFINITION
// ---------------------------------------------------------------------------

const ALEX_PALETTE: Readonly<Record<string, string>> = {
  // Hair (Bright ginger / orange)
  "0": "#A84C18", // Dark auburn shadow
  "1": "#C85B1E", // Medium shadow ginger
  "2": "#DE6E27", // Base vibrant ginger/orange
  "3": "#F08438", // Highlight orange
  // Skin (Pale peach)
  "4": "#DFA484", // Pale skin shadow
  "5": "#E8B294", // Mid peach
  "6": "#F4C2A5", // Base pale peach skin
  "7": "#FCD2BA", // Skin highlight
  // Eyes (Emerald green)
  "W": "#FFFFFF", // Sclera white
  "G": "#1E8A38", // Emerald green pupil
  "L": "#EE7C8E", // Pink lips
  // Tunic (Moss green)
  "8": "#527E3B", // Tunic shadow
  "9": "#669A4A", // Base moss green
  "A": "#78AE5B", // Tunic highlight
  "C": "#446432", // Sleeve dark olive cuff
  "K": "#25441C", // Dark forest green belt
  // Pants (Warm brown)
  "D": "#3E2310", // Dark brown shadow
  "F": "#523218", // Base warm brown trousers
  "J": "#663F22", // Highlight brown
  // Boots (Slate / Charcoal gray)
  "X": "#2F3237", // Dark charcoal
  "Y": "#41444A", // Slate gray
  "Z": "#54575E", // Light slate
};

export function createAlexTextures(): CharacterSkinTextures {
  const headFront = makeTexture(
    8,
    8,
    [
      "23222232",
      "22222222",
      "22666662",
      "26WG6GW2",
      "66655666",
      "666LL666",
      "66666666",
      "66666666",
    ],
    ALEX_PALETTE,
  );

  const headBack = makeTexture(
    8,
    8,
    [
      "22322322",
      "23222232",
      "12222221",
      "22322322",
      "22222222",
      "23222232",
      "12222221",
      "01222210",
    ],
    ALEX_PALETTE,
  );

  const headSide = makeTexture(
    8,
    8,
    [
      "22222222",
      "23222232",
      "23222222",
      "22666622",
      "22666662",
      "12266662",
      "01226662",
      "00122662",
    ],
    ALEX_PALETTE,
  );

  const headTop = makeTexture(
    8,
    8,
    [
      "23222232",
      "32233223",
      "22333322",
      "23333332",
      "23333332",
      "22333322",
      "32233223",
      "23222232",
    ],
    ALEX_PALETTE,
  );

  const headBottom = makeTexture(
    8,
    8,
    [
      "66666666",
      "66666666",
      "66666666",
      "66666666",
      "66666666",
      "66666666",
      "01222210",
      "00122100",
    ],
    ALEX_PALETTE,
  );

  // Alex torso has the ginger hair lock hanging down the left chest (columns 1-2):
  const torsoFront = makeTexture(
    8,
    12,
    [
      "99A66A99",
      "99966999",
      "92299A99",
      "92399998",
      "82299999",
      "9999A999",
      "99999998",
      "KKKKKKKK",
      "KKKKKKKK",
      "99A99998",
      "899999A9",
      "89999998",
    ],
    ALEX_PALETTE,
  );

  const torsoBack = makeTexture(
    8,
    12,
    [
      "89999998",
      "99999999",
      "A999999A",
      "99999999",
      "99989999",
      "99999999",
      "89999998",
      "KKKKKKKK",
      "KKKKKKKK",
      "A999999A",
      "99989999",
      "89999998",
    ],
    ALEX_PALETTE,
  );

  const torsoSide = makeTexture(
    4,
    12,
    [
      "8999",
      "9999",
      "A999",
      "9998",
      "9999",
      "8999",
      "9998",
      "KKKK",
      "KKKK",
      "A999",
      "8999",
      "9998",
    ],
    ALEX_PALETTE,
  );

  const torsoTopBottom = makeTexture(
    8,
    4,
    [
      "99999999",
      "A999999A",
      "89999998",
      "99999999",
    ],
    ALEX_PALETTE,
  );

  // Alex has slim arms (3px wide)
  const armFront = makeTexture(
    3,
    12,
    [
      "9A9",
      "998",
      "A99",
      "CCC",
      "666",
      "676",
      "656",
      "666",
      "676",
      "656",
      "666",
      "565",
    ],
    ALEX_PALETTE,
  );

  const armSide = makeTexture(
    4,
    12,
    [
      "9998",
      "8999",
      "9998",
      "CCCC",
      "6666",
      "6667",
      "6566",
      "6666",
      "6676",
      "5666",
      "6666",
      "5555",
    ],
    ALEX_PALETTE,
  );

  const armTopBottom = makeTexture(
    3,
    4,
    [
      "999",
      "A99",
      "998",
      "999",
    ],
    ALEX_PALETTE,
  );

  const armHandBottom = makeTexture(
    3,
    4,
    [
      "666",
      "676",
      "656",
      "666",
    ],
    ALEX_PALETTE,
  );

  const legFront = makeTexture(
    4,
    12,
    [
      "DFFF",
      "FFFD",
      "JFFF",
      "FFFF",
      "DFFF",
      "FFFD",
      "XYYX",
      "YYYX",
      "XYYX",
      "YYYX",
      "XYYX",
      "YZZY",
    ],
    ALEX_PALETTE,
  );

  const legSide = makeTexture(
    4,
    12,
    [
      "FFFD",
      "DFFF",
      "FFFD",
      "FFFF",
      "FFFD",
      "DFFF",
      "YXXY",
      "XYYX",
      "YXXY",
      "XYYX",
      "YXXY",
      "ZZZZ",
    ],
    ALEX_PALETTE,
  );

  const legTopBottom = makeTexture(
    4,
    4,
    [
      "FFFF",
      "JFFF",
      "FFFD",
      "FFFF",
    ],
    ALEX_PALETTE,
  );

  const fpArm = makeTexture(
    3,
    12,
    [
      "666",
      "676",
      "656",
      "666",
      "676",
      "656",
      "666",
      "676",
      "656",
      "666",
      "565",
      "555",
    ],
    ALEX_PALETTE,
  );

  return {
    headFront,
    headBack,
    headSide,
    headTop,
    headBottom,
    torsoFront,
    torsoBack,
    torsoSide,
    torsoTopBottom,
    armFront,
    armSide,
    armTopBottom,
    armHandBottom,
    legFront,
    legSide,
    legTopBottom,
    fpArm,
  };
}
